import { DialogContent } from '../../components/ui/DialogContent';
import { useState } from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import { ResponsiveDialog } from '../../components/ui/ResponsiveDialog';
import { DialogHeader } from '../../components/ui/DialogHeader';
import { DialogActions } from '../../components/ui/DialogActions';
import { useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard';

interface AccountSectionProps {
  changePassword: (current: string, next: string) => void;
  embedded?: boolean;
  onDismiss?: () => void;
  open: boolean;
  onClose: () => void;
}

export function AccountSection({
  changePassword,
  open,
  onClose,
  embedded = false,
  onDismiss,
}: AccountSectionProps) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setCurrent('');
    setNext('');
    setConfirmation('');
    setError(null);
  };
  const close = onClose;
  const { requestClose, closeAfterSave } = useUnsavedChangesGuard({
    open,
    scopeKey: 'app.child',
    dirty: current !== '' || next !== '' || confirmation !== '',
    onDiscard: reset,
    onClose: close,
  });
  const requestDialogClose = () => {
    void requestClose();
  };

  const submit = () => {
    if (!current || !next || !confirmation)
      return setError('Please fill in all fields.');
    if (next !== confirmation) return setError('New passwords do not match.');
    if (next.length < 6)
      return setError('New password must be at least 6 characters long.');
    changePassword(current, next);
    reset();
    closeAfterSave();
  };

  const content = (
    <>
      <DialogHeader
        id="change-password-title"
        title="Change password"
        onClose={onDismiss || requestDialogClose}
        onBack={embedded ? requestDialogClose : undefined}
      />
      <DialogContent>
        <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
          <TextField
            type="password"
            autoComplete="current-password"
            label="Current password"
            value={current}
            onChange={(event) => setCurrent(event.target.value)}
          />
          <TextField
            type="password"
            autoComplete="new-password"
            label="New password"
            value={next}
            onChange={(event) => setNext(event.target.value)}
          />
          <TextField
            type="password"
            autoComplete="new-password"
            label="Confirm new password"
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
          />
          {error && (
            <Typography color="error" role="alert">
              {error}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={requestDialogClose}>Cancel</Button>
        <Button variant="contained" onClick={submit}>
          Update
        </Button>
      </DialogActions>
    </>
  );
  return embedded ? (
    content
  ) : (
    <ResponsiveDialog
      open={open}
      onClose={requestDialogClose}
      aria-labelledby="change-password-title"
      maxWidth="xs"
    >
      {content}
    </ResponsiveDialog>
  );
}
